import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Chip,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  Input as InputIcon,
  Output as OutputIcon,
  Security as SecurityIcon,
  PlayArrow as PlayIcon,
  BugReport as DebugIcon,
  Build as ToolsIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { expertApi } from '../../services/api';
import type { CodeExecutorConfig } from '../../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`doc-tabpanel-${index}`}
      aria-labelledby={`doc-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

interface CodeBlockProps {
  code: string;
  title?: string;
}

const CodeBlock: React.FC<CodeBlockProps> = ({ code, title }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        my: 2, 
        overflow: 'hidden',
        bgcolor: '#1e1e1e',
      }}
    >
      {title && (
        <Box 
          sx={{ 
            px: 2, 
            py: 1, 
            bgcolor: '#2d2d2d', 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" sx={{ color: '#ccc' }}>
            {title}
          </Typography>
          <IconButton size="small" onClick={handleCopy} sx={{ color: '#888' }}>
            <CopyIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          overflow: 'auto',
          fontFamily: '"Fira Code", "Consolas", monospace',
          fontSize: '0.85rem',
          lineHeight: 1.6,
          color: '#d4d4d4',
        }}
      >
        <code>{code}</code>
      </Box>
    </Paper>
  );
};

// Tool info interface
interface ToolInfo {
  name: string;
  description: string;
  category: string;
  input_schema: Record<string, any>;
  requires_confirmation: boolean;
  danger_level: number;
}

interface AvailableToolsResponse {
  tools: ToolInfo[];
  categories: string[];
  total_count: number;
}

interface CodeExpertDocumentationProps {
  open: boolean;
  onClose: () => void;
}

export const CodeExpertDocumentation: React.FC<CodeExpertDocumentationProps> = ({
  open,
  onClose,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [toolSearch, setToolSearch] = useState('');
  const [toolCategory, setToolCategory] = useState('');

  // Fetch executor config from API
  const { data: config, isLoading, error } = useQuery({
    queryKey: ['code-executor-config'],
    queryFn: () => expertApi.getCodeExecutorConfig(),
    enabled: open, // Only fetch when dialog is open
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch available tools from API
  const { data: toolsData, isLoading: toolsLoading } = useQuery({
    queryKey: ['code-expert-available-tools'],
    queryFn: async (): Promise<AvailableToolsResponse> => {
      const result = await expertApi.getAvailableTools();
      return result as AvailableToolsResponse;
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Filter tools based on search and category
  const filteredTools = (toolsData?.tools ?? []).filter((tool: ToolInfo) => {
    const matchesSearch = !toolSearch || 
      tool.name.toLowerCase().includes(toolSearch.toLowerCase()) ||
      tool.description.toLowerCase().includes(toolSearch.toLowerCase());
    const matchesCategory = !toolCategory || tool.category === toolCategory;
    return matchesSearch && matchesCategory;
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <CodeIcon color="warning" />
            <Typography variant="h6">CODE Expert Documentation</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">
            <AlertTitle>Error Loading Configuration</AlertTitle>
            Failed to load executor configuration. Please try again.
          </Alert>
        ) : (
          <>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab icon={<PlayIcon />} label="Getting Started" iconPosition="start" />
              <Tab icon={<InputIcon />} label="Inputs" iconPosition="start" />
              <Tab icon={<OutputIcon />} label="Outputs" iconPosition="start" />
              <Tab icon={<ToolsIcon />} label="Tool Calling" iconPosition="start" />
              <Tab icon={<CodeIcon />} label="Available Modules" iconPosition="start" />
              <Tab icon={<DebugIcon />} label="Examples" iconPosition="start" />
              <Tab icon={<SecurityIcon />} label="Security & Limits" iconPosition="start" />
            </Tabs>

            {/* Getting Started Tab */}
            <TabPanel value={tabValue} index={0}>
              <Typography variant="h6" gutterBottom>What is a CODE Expert?</Typography>
              <Typography paragraph>
                A CODE expert executes Python code directly instead of calling an LLM. This is useful for:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <li><Typography>Data processing and transformation</Typography></li>
                <li><Typography>Mathematical calculations</Typography></li>
                <li><Typography>Combining outputs from multiple upstream experts</Typography></li>
                <li><Typography>Custom business logic that doesn't require AI</Typography></li>
                <li><Typography>Fast, deterministic operations with zero LLM cost</Typography></li>
                <li><Typography><strong>Calling agent tools</strong> (trading, data fetching, etc.)</Typography></li>
              </Box>

              <Alert severity="info" sx={{ my: 2 }}>
                <AlertTitle>No LLM Required</AlertTitle>
                CODE experts don't need an LLM model. They execute Python code directly on the server,
                making them fast, deterministic, and cost-free.
              </Alert>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Basic Structure</Typography>
              <Typography paragraph>
                Every CODE expert follows this pattern:
              </Typography>

              <CodeBlock
                title="Basic CODE Expert Structure"
                code={`# 1. Access your inputs (automatically injected as variables)
value = market_data.get('close', 0)

# 2. Process the data
processed = value * 1.1

# 3. Set the result variable (REQUIRED)
result = {
    'processed_value': processed,
    'status': 'success'
}`}
              />

              <Alert severity="warning" sx={{ my: 2 }}>
                <AlertTitle>Important: Set the result Variable</AlertTitle>
                Your code <strong>must</strong> set the <code>result</code> variable with a dictionary.
                This dictionary becomes the expert's output and can be used by downstream experts.
              </Alert>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Workflow Integration</Typography>
              <Typography paragraph>
                CODE experts integrate seamlessly into your AI System workflow:
              </Typography>
              <Box component="ol" sx={{ pl: 3 }}>
                <li><Typography>Define inputs that receive data from upstream experts</Typography></li>
                <li><Typography>Process the data using Python code</Typography></li>
                <li><Typography>Return results that downstream experts can consume</Typography></li>
              </Box>
            </TabPanel>

            {/* Inputs Tab */}
            <TabPanel value={tabValue} index={1}>
              <Typography variant="h6" gutterBottom>Working with Inputs</Typography>
              <Typography paragraph>
                Inputs are automatically injected as Python variables in your code's execution namespace.
              </Typography>

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                <strong>How Inputs Work</strong>
              </Typography>
              <Box component="ol" sx={{ pl: 3 }}>
                <li><Typography>Define inputs in the "Input Variables" section</Typography></li>
                <li><Typography>Each input name becomes a Python variable</Typography></li>
                <li><Typography>Values come from upstream expert outputs or static data</Typography></li>
              </Box>

              <CodeBlock
                title="Accessing Inputs"
                code={`# If you have an input named "market_data":
price = market_data['close']
volume = market_data.get('volume', 0)

# If you have an input named "analysis_result":
signal = analysis_result.get('signal', 'HOLD')
confidence = analysis_result.get('confidence', 0.5)

# Access all inputs as a dictionary:
for name, value in inputs.items():
    print(f"Input {name}: {type(value)}")`}
              />

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                <strong>Available Special Variables</strong>
              </Typography>
              {config && (
                <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Variable</strong></TableCell>
                        <TableCell><strong>Description</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(config.special_variables).map(([name, desc]) => (
                        <TableRow key={name}>
                          <TableCell><code>{name}</code></TableCell>
                          <TableCell>{desc}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                <strong>Input Types</strong>
              </Typography>
              <Typography paragraph>
                You can use various input handlers to feed data into your CODE expert:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <li><Typography><strong>previous_expert_result</strong> - Output from an upstream expert</Typography></li>
                <li><Typography><strong>static</strong> - Fixed configuration values</Typography></li>
                <li><Typography><strong>price_data</strong> - Market price data</Typography></li>
                <li><Typography><strong>indicator_data</strong> - Technical indicator values</Typography></li>
              </Box>
            </TabPanel>

            {/* Outputs Tab */}
            <TabPanel value={tabValue} index={2}>
              <Typography variant="h6" gutterBottom>Returning Results</Typography>
              <Typography paragraph>
                Your code must set the <code>result</code> variable with a dictionary containing your output.
              </Typography>

              <CodeBlock
                title="Basic Output"
                code={`# Simple output
result = {
    'processed_value': 123.45,
    'status': 'success'
}

# Output with nested data
result = {
    'analysis': {
        'signal': 'BUY',
        'confidence': 0.85,
        'reasons': ['trend_up', 'volume_high']
    },
    'metadata': {
        'processed_at': datetime.now().isoformat(),
        'input_count': len(inputs)
    }
}`}
              />

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                <strong>Output Schema Validation</strong>
              </Typography>
              <Typography paragraph>
                If you define an output schema for your CODE expert, the result will be validated against it.
                This ensures your code produces the expected structure.
              </Typography>

              <Alert severity="info" sx={{ my: 2 }}>
                <AlertTitle>Validation is Optional but Recommended</AlertTitle>
                Defining an output schema helps catch errors early and documents what your expert produces.
                Validation warnings are logged but won't prevent execution.
              </Alert>

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                <strong>Supported Output Types</strong>
              </Typography>
              <Typography paragraph>
                The result is automatically serialized to JSON. These types are supported:
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Python Type</strong></TableCell>
                      <TableCell><strong>JSON Output</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow><TableCell>str, int, float, bool</TableCell><TableCell>As-is</TableCell></TableRow>
                    <TableRow><TableCell>dict</TableCell><TableCell>JSON object</TableCell></TableRow>
                    <TableRow><TableCell>list, tuple</TableCell><TableCell>JSON array</TableCell></TableRow>
                    <TableRow><TableCell>Decimal</TableCell><TableCell>float</TableCell></TableRow>
                    <TableRow><TableCell>datetime, date</TableCell><TableCell>ISO format string</TableCell></TableRow>
                    <TableRow><TableCell>numpy.ndarray</TableCell><TableCell>nested list</TableCell></TableRow>
                    <TableRow><TableCell>pandas.DataFrame</TableCell><TableCell>list of records</TableCell></TableRow>
                    <TableRow><TableCell>pandas.Series</TableCell><TableCell>dict</TableCell></TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Tool Calling Tab */}
            <TabPanel value={tabValue} index={3}>
              <Typography variant="h6" gutterBottom>Calling Agent Tools</Typography>
              <Typography paragraph>
                CODE experts can call any registered agent tool using the <code>call_tool()</code> function.
                This gives you access to trading operations, data fetching, and more - all from Python code.
              </Typography>

              <Alert severity="success" sx={{ my: 2 }}>
                <AlertTitle>All Tools Available</AlertTitle>
                Every tool that agents can use is also available to CODE experts. You can call tools for
                trading accounts, price data, executing trades, managing experts, and more.
              </Alert>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>How to Use call_tool()</Typography>
              <CodeBlock
                title="Basic Tool Calling Syntax"
                code={`# Call a tool with parameters
result = call_tool('tool_name', {'param1': 'value1', 'param2': 'value2'})

# Result structure
# {
#     'success': True/False,
#     'data': <tool output>,
#     'error': None or 'error message',
#     'metadata': {}
# }

# Always check for success
if result['success']:
    data = result['data']
    # Process the data
else:
    error = result['error']
    # Handle the error`}
              />

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Example: Getting Trading Accounts</Typography>
              <CodeBlock
                title="Fetch Trading Accounts"
                code={`# Get all trading accounts
accounts_result = call_tool('list_trading_accounts', {})

if accounts_result['success']:
    accounts = accounts_result['data'].get('accounts', [])
    
    # Find active accounts
    active_accounts = [a for a in accounts if a.get('is_active')]
    
    result = {
        'accounts': accounts,
        'active_count': len(active_accounts),
        'total_count': len(accounts)
    }
else:
    result = {'error': accounts_result['error']}`}
              />

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Example: Fetching Price Data</Typography>
              <CodeBlock
                title="Get Price Data with Tool"
                code={`# First get an account
accounts = call_tool('list_trading_accounts', {})
account_id = accounts['data']['accounts'][0]['id']

# Fetch price data
prices = call_tool('fetch_price_data', {
    'symbol': 'EURUSD',
    'timeframe': 'H1',
    'count': 100,
    'account_id': account_id
})

if prices['success']:
    price_data = prices['data']
    closes = price_data.get('close', [])
    
    # Calculate simple moving average
    if len(closes) >= 20:
        sma_20 = sum(closes[-20:]) / 20
    else:
        sma_20 = sum(closes) / len(closes) if closes else 0
    
    result = {
        'symbol': 'EURUSD',
        'latest_close': closes[-1] if closes else None,
        'sma_20': sma_20,
        'data_points': len(closes)
    }
else:
    result = {'error': prices['error']}`}
              />

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>Available Tools ({toolsData?.total_count ?? 0})</Typography>
              
              {/* Search and Filter */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search tools..."
                  value={toolSearch}
                  onChange={(e) => setToolSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flexGrow: 1 }}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={toolCategory}
                    onChange={(e) => setToolCategory(e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {(toolsData?.categories ?? []).map((cat: string) => (
                      <MenuItem key={cat} value={cat}>{cat.replace(/_/g, ' ')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {toolsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {filteredTools.map((tool: ToolInfo) => (
                    <Accordion key={tool.name} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Typography fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                            {tool.name}
                          </Typography>
                          <Chip 
                            label={tool.category.replace(/_/g, ' ')} 
                            size="small" 
                            variant="outlined"
                            color="primary"
                          />
                          {tool.danger_level > 2 && (
                            <Chip label="⚠️ Dangerous" size="small" color="warning" />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography paragraph sx={{ color: 'text.secondary' }}>
                          {tool.description}
                        </Typography>
                        
                        {tool.input_schema.properties && Object.keys(tool.input_schema.properties).length > 0 && (
                          <>
                            <Typography variant="subtitle2" gutterBottom>Parameters:</Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell><strong>Name</strong></TableCell>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell><strong>Required</strong></TableCell>
                                    <TableCell><strong>Description</strong></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {Object.entries(tool.input_schema.properties).map(([name, prop]: [string, any]) => (
                                    <TableRow key={name}>
                                      <TableCell><code>{name}</code></TableCell>
                                      <TableCell>{prop.type || 'any'}</TableCell>
                                      <TableCell>
                                        {tool.input_schema.required?.includes(name) ? '✓' : ''}
                                      </TableCell>
                                      <TableCell sx={{ maxWidth: 300, fontSize: '0.8rem' }}>
                                        {prop.description?.slice(0, 100)}
                                        {prop.description?.length > 100 ? '...' : ''}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        )}
                        
                        <CodeBlock
                          title={`Example: calling ${tool.name}`}
                          code={`result = call_tool('${tool.name}', {${
                            tool.input_schema.required?.length 
                              ? '\n    # Required parameters:\n' + 
                                tool.input_schema.required.map((r: string) => `    '${r}': ...`).join(',\n') + '\n'
                              : ''
                          }})`}
                        />
                      </AccordionDetails>
                    </Accordion>
                  ))}
                  
                  {filteredTools.length === 0 && (
                    <Alert severity="info">
                      No tools found matching your search criteria.
                    </Alert>
                  )}
                </Box>
              )}
            </TabPanel>

            {/* Available Modules Tab - Dynamic from API */}
            <TabPanel value={tabValue} index={4}>
              <Typography variant="h6" gutterBottom>Pre-imported Modules</Typography>
              <Typography paragraph>
                These modules are automatically available without importing:
              </Typography>

              {config && (
                <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Variable</strong></TableCell>
                        <TableCell><strong>Description</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(config.pre_injected_modules).map(([name, desc]) => (
                        <TableRow key={name}>
                          <TableCell><code>{name}</code></TableCell>
                          <TableCell>{desc}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Optional Modules</Typography>
              <Typography paragraph>
                These are pre-imported if installed on the server:
              </Typography>

              {config && (
                <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Module</strong></TableCell>
                        <TableCell><strong>Description</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(config.optional_modules).map(([name, desc]) => (
                        <TableRow key={name}>
                          <TableCell><code>{name}</code></TableCell>
                          <TableCell>{desc}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Allowed Imports</Typography>
              <Typography paragraph>
                You can import additional modules from this whitelist:
              </Typography>

              {config && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 2 }}>
                  {config.allowed_imports.map((mod) => (
                    <Chip key={mod} label={mod} size="small" variant="outlined" />
                  ))}
                </Box>
              )}

              {config && Object.keys(config.import_aliases).length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Import Aliases
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    These aliases are supported for convenience:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }}>
                    {Object.entries(config.import_aliases).map(([alias, module]) => (
                      <Chip 
                        key={alias} 
                        label={`${alias} → ${module}`} 
                        size="small" 
                        variant="outlined"
                        color="info"
                      />
                    ))}
                  </Box>
                </>
              )}

              <CodeBlock
                title="Import Example"
                code={`import statistics
import re
from collections import defaultdict
from datetime import timedelta

# Use imported modules
prices = [100, 102, 98, 105, 103]
avg = statistics.mean(prices)
std = statistics.stdev(prices)

result = {
    'average': avg,
    'std_dev': std
}`}
              />

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Safe Built-in Functions</Typography>
              <Typography paragraph>
                These Python built-ins are available:
              </Typography>

              {config && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Types</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {config.safe_builtins.types.map((name) => (
                      <Chip key={name} label={name} size="small" variant="outlined" color="primary" />
                    ))}
                  </Box>

                  <Typography variant="subtitle2" gutterBottom>Functions</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {config.safe_builtins.functions.map((name) => (
                      <Chip key={name} label={name} size="small" variant="outlined" color="success" />
                    ))}
                  </Box>

                  <Typography variant="subtitle2" gutterBottom>Exceptions (for error handling)</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {config.safe_builtins.exceptions.map((name) => (
                      <Chip key={name} label={name} size="small" variant="outlined" color="warning" />
                    ))}
                  </Box>
                </>
              )}
            </TabPanel>

            {/* Examples Tab */}
            <TabPanel value={tabValue} index={5}>
              <Typography variant="h6" gutterBottom>Practical Examples</Typography>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Example 1: Simple Data Processing</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography paragraph>
                    Process market data and calculate basic statistics:
                  </Typography>
                  <CodeBlock
                    code={`# Input: market_data with OHLCV data
close_prices = market_data.get('close', [])

if close_prices:
    avg_price = sum(close_prices) / len(close_prices)
    max_price = max(close_prices)
    min_price = min(close_prices)
    
    result = {
        'average': avg_price,
        'high': max_price,
        'low': min_price,
        'range': max_price - min_price,
        'count': len(close_prices)
    }
else:
    result = {
        'error': 'No price data available',
        'count': 0
    }`}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Example 2: Combining Multiple Expert Outputs</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography paragraph>
                    Aggregate signals from multiple analysis experts:
                  </Typography>
                  <CodeBlock
                    code={`# Inputs: technical_analysis, sentiment_analysis, fundamental_analysis

signals = {
    'technical': technical_analysis.get('signal', 'NEUTRAL'),
    'sentiment': sentiment_analysis.get('signal', 'NEUTRAL'),
    'fundamental': fundamental_analysis.get('signal', 'NEUTRAL')
}

# Weight each signal
weights = {'technical': 0.4, 'sentiment': 0.3, 'fundamental': 0.3}
signal_values = {'BUY': 1, 'NEUTRAL': 0, 'SELL': -1}

weighted_score = sum(
    signal_values.get(signals[key], 0) * weight
    for key, weight in weights.items()
)

# Determine final signal
if weighted_score > 0.3:
    final_signal = 'BUY'
elif weighted_score < -0.3:
    final_signal = 'SELL'
else:
    final_signal = 'HOLD'

result = {
    'signal': final_signal,
    'confidence': abs(weighted_score),
    'component_signals': signals,
    'weighted_score': weighted_score
}`}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Example 3: Using Pandas for Analysis</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography paragraph>
                    Use pandas for more complex data analysis:
                  </Typography>
                  <CodeBlock
                    code={`import pandas as pd

# Input: market_data as dict with lists
df = pd.DataFrame(market_data)

# Calculate moving averages
df['sma_10'] = df['close'].rolling(10).mean()
df['sma_20'] = df['close'].rolling(20).mean()

# Generate signals
df['signal'] = 'HOLD'
df.loc[df['sma_10'] > df['sma_20'], 'signal'] = 'BUY'
df.loc[df['sma_10'] < df['sma_20'], 'signal'] = 'SELL'

# Get latest values
latest = df.iloc[-1]

result = {
    'current_price': float(latest['close']),
    'sma_10': float(latest['sma_10']) if pd.notna(latest['sma_10']) else None,
    'sma_20': float(latest['sma_20']) if pd.notna(latest['sma_20']) else None,
    'signal': latest['signal'],
    'data_points': len(df)
}`}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Example 4: Error Handling</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography paragraph>
                    Robust error handling pattern:
                  </Typography>
                  <CodeBlock
                    code={`try:
    # Validate inputs
    if not market_data:
        raise ValueError("market_data is required")
    
    close = market_data.get('close')
    if not close or not isinstance(close, list):
        raise ValueError("market_data.close must be a list")
    
    if len(close) < 10:
        raise ValueError(f"Need at least 10 data points, got {len(close)}")
    
    # Process data
    latest_price = close[-1]
    avg_price = sum(close[-10:]) / 10
    change_pct = ((latest_price - avg_price) / avg_price) * 100
    
    result = {
        'status': 'success',
        'latest_price': latest_price,
        'avg_10': avg_price,
        'change_percent': round(change_pct, 2)
    }

except ValueError as e:
    result = {
        'status': 'error',
        'error_type': 'validation',
        'message': str(e)
    }

except Exception as e:
    result = {
        'status': 'error',
        'error_type': 'unexpected',
        'message': f"Unexpected error: {e}"
    }`}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Example 5: Using Context Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography paragraph>
                    Access execution context for conditional logic:
                  </Typography>
                  <CodeBlock
                    code={`# Access context information
expert_name = context.get('expert_name', 'unknown')
is_backtest = context.get('backtesting_mode', False)
user_id = context.get('user_id')

# Conditional logic based on context
if is_backtest:
    # Use historical simulation mode
    execution_mode = 'simulation'
    slippage = 0.001  # 0.1% simulated slippage
else:
    # Live mode
    execution_mode = 'live'
    slippage = 0

# Include context in output
result = {
    'processed_by': expert_name,
    'execution_mode': execution_mode,
    'slippage_applied': slippage,
    'is_backtest': is_backtest,
    'data': {
        # ... your processed data
    }
}`}
                  />
                </AccordionDetails>
              </Accordion>
            </TabPanel>

            {/* Security Tab - Dynamic from API */}
            <TabPanel value={tabValue} index={6}>
              <Typography variant="h6" gutterBottom>Security Restrictions</Typography>
              <Typography paragraph>
                CODE experts run in a sandboxed environment with restricted capabilities for security:
              </Typography>

              {config && (
                <Alert severity="error" sx={{ my: 2 }}>
                  <AlertTitle>Forbidden Operations</AlertTitle>
                  <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                    {config.forbidden_operations.map((op, idx) => (
                      <li key={idx}>{op}</li>
                    ))}
                  </Box>
                </Alert>
              )}

              {config && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                    <strong>Blocked Patterns</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    The following patterns are detected and blocked:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, my: 1 }}>
                    {config.forbidden_patterns.map((pattern) => (
                      <Chip 
                        key={pattern} 
                        label={pattern} 
                        size="small" 
                        variant="outlined"
                        color="error"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    ))}
                  </Box>
                </>
              )}

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Execution Limits</Typography>
              {config && (
                <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Limit</strong></TableCell>
                        <TableCell><strong>Value</strong></TableCell>
                        <TableCell><strong>Configurable</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Default Timeout</TableCell>
                        <TableCell>{config.limits.default_timeout} seconds</TableCell>
                        <TableCell>Yes</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Minimum Timeout</TableCell>
                        <TableCell>{config.limits.min_timeout} second(s)</TableCell>
                        <TableCell>-</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Maximum Timeout</TableCell>
                        <TableCell>{config.limits.max_timeout} seconds</TableCell>
                        <TableCell>-</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Memory</TableCell>
                        <TableCell>Limited by server</TableCell>
                        <TableCell>No</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Alert severity="warning" sx={{ my: 2 }}>
                <AlertTitle>Timeout Handling</AlertTitle>
                If your code exceeds the timeout, execution is terminated and an error result is returned.
                Optimize long-running operations or increase the timeout setting.
              </Alert>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Best Practices</Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <li><Typography>Always validate input data before processing</Typography></li>
                <li><Typography>Use try/except blocks for robust error handling</Typography></li>
                <li><Typography>Keep code simple and focused on data processing</Typography></li>
                <li><Typography>Avoid infinite loops or recursive calls without limits</Typography></li>
                <li><Typography>Test your code with various input scenarios</Typography></li>
                <li><Typography>Use the output schema to document expected results</Typography></li>
              </Box>
            </TabPanel>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CodeExpertDocumentation;
